import { Dialog, DialogContent, Typography, Divider, Box, IconButton, useTheme, Drawer, CircularProgress, Button, useMediaQuery, Slide } from '@mui/material'
import { useAssignmentStore } from "../../store/assignment.store"
import noPermissionImg from '../../assets/no-permission.svg'
import Download from "@mui/icons-material/Download"
import { useSearchParams } from 'react-router-dom'
import { Icons, SPACES_LOCATION_REGEXP, attachmentThumbnails, convertToCDNLink, isIOS, formatDate, getTimePassedFrom, ASSIGNMENT_CONVERT_DUE_TIME_BEFORE } from "../../utils/utilities"
import { toast } from "react-toastify"
import PropTypes from 'prop-types'
import { OpenInNew, WarningAmberRounded } from '@mui/icons-material'
import api from '../../service/api'
import { forwardRef, useState } from 'react'
import DocumentViewer from './DocumentViewer'
import ConfirmDialog from './ConfirmDialog'
import { useUserStore } from '../../store/user.store'
import CountCard from './CountCard'
import CustomChip from './CustomChip'
import dayjs from 'dayjs'

const extentionsToNameMap = {
    'pdf': 'PDF',
    'docx': 'Document',
    'xslx': 'Spreadsheet',
    'jpg': 'Image',
    'jpeg': 'Image',
    'png': 'Image'
}

/**
 * AssignmentViewer component displays assignment details, attachments, and allows actions such as download, preview, and delete.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.onClose - Callback to close the viewer dialog
 * @param {Object} props.selectedValue - The selected assignment object to display
 * @param {Boolean} props.open - Whether the dialog is open
 * @param {Boolean} props.loaded - Whether the assignment data is loaded
 * @param {Function} [props.onAfterDelete] - Optional callback after assignment deletion
 * @returns {JSX.Element} Assignment details dialog with file actions
 */


const AssignmentViewer = ( { onClose, selectedValue, open, loaded, onAfterDelete } ) => {

    const [previewFile, setPreviewFile] = useState( null )
    const [deleting, setDeleting] = useState( false )
    const [downloadingFile, setDownloadingFile] = useState( null )
    const [previewingFileName, setPreviewingFileName] = useState( null )
    const [confirmDialogStatus, setConfirmDialogStatus] = useState( false )

    const AssignmentStore = useAssignmentStore()
    const UserStore = useUserStore()

    const { palette, border, breakpoints, layout } = useTheme()
    const [searchParams] = useSearchParams( { searchText: '', selectedAssignmentId: null, open: false } )
    const selectedAssignmentId = searchParams.get( 'selectedAssignmentId' )

    const fullscreen = useMediaQuery( breakpoints.down( 'sm' ) )

    const handleClose = () => {
        onClose( selectedValue )
    }

    const handleListItemClick = async ( value ) => {
        try {
            setDownloadingFile( value )
            const fileDetails = value.match( SPACES_LOCATION_REGEXP ).groups
            await toast.promise( AssignmentStore.downloadAssignment( value ), { success: `${decodeURIComponent( fileDetails.filefullname )} downloaded successfully`, pending: `Downloading ${decodeURIComponent( fileDetails.filefullname )}...` } )
        } catch ( err ) {
            console.log( err )
            toast( `Failed to download the attachment!` )
        } finally {
            setDownloadingFile( null )
        }
    }

    const deleteAssignment = async () => {
        setDeleting( true )
        let payload = { "assignment_id": selectedValue.assignment_id }
        if ( await AssignmentStore.deleteAssignment( payload, true ) ) {
            setConfirmDialogStatus( false )
            onClose( selectedValue )
        } if ( onAfterDelete )
            onAfterDelete( selectedValue )
        setDeleting( false )
    }

    const viewAttachment = async ( value ) => {
        try {
            const { data: { data } } = await api.downloadAssignment( value, true )
            const fileDetails = value.match( SPACES_LOCATION_REGEXP ).groups
            setPreviewingFileName( decodeURIComponent( fileDetails.filefullname ) )
            setPreviewFile( convertToCDNLink( data ) )
        } catch ( err ) {
            console.log( err )
            toast( `An error occurred while viewing the submission!` )
        }
    }

    const isAssignmentActive = dayjs( selectedValue?.assignment_due_date ) > dayjs()

    return (
        <Dialog
            fullScreen={fullscreen}
            slotProps={{
                paper: {
                    sx: {
                        width: { sm: "90%", xs: "100%" },
                        maxWidth: "720px",
                        height: "fit-content",
                        maxHeight: "calc(100% - 200px)"
                    }
                },
                container: {
                    sx: { alignItems: { sm: "center", xs: "flex-end" } }
                }
            }}
            maxWidth="lg"
            onClose={handleClose}
            aria-labelledby="assignment-viewer"
            open={open}
        >
            {loaded && selectedValue && confirmDialogStatus && <ConfirmDialog secondaryConfirmationText='remove-submissions-and-delete' enableSecondaryConfirmation waiting={deleting} status={confirmDialogStatus} confirmAction={deleteAssignment} cancelAction={() => setConfirmDialogStatus( false )} message={<p> Assignment <u><strong><em>{selectedValue.assignment_name}</em></strong></u> will be deleted permanently. </p>} actionName="Yes, Delete it" />
            }
            < Drawer anchor='bottom' sx={{ zIndex: 9000000, }} open={Boolean( previewFile )}
                slotProps={{
                    paper: {
                        sx: {
                            height: "calc(100% - 50px)",
                            border: border[1]
                        }
                    }
                }} >
                <Box bgcolor={palette.cardBackground} flexGrow={1} display="flex" overflow="auto" flexDirection="column" borderRadius="20px 20px 0 0">
                    <Box borderBottom={border[1]} display="flex" alignItems="center" justifyContent="space-between" gap="20px" padding="10px 20px">
                        <Typography variant="h1" fontWeight="600" component="p" fontSize="16px">{previewingFileName}</Typography>
                        <Box display="flex" alignItems="center" gap="10px" >
                            <IconButton size='small' onClick={() => setPreviewFile( null )}>
                                {Icons.default.CloseIcon}
                            </IconButton>
                        </Box>
                    </Box>
                    <Box display="flex" padding="10px" flexDirection="column" overflow="auto" flexGrow={1} height="300px">
                        <DocumentViewer type={previewingFileName?.split( "." ).pop()} setFile={setPreviewFile} loadingText={`Loading ${previewingFileName}...`} file={previewFile} />
                    </Box>
                </Box>
            </Drawer >

            {loaded && selectedValue && !confirmDialogStatus && <Box>
                <Box display="flex" alignItems="center" justifyContent="space-between" padding={{ sm: "20px", xs: "10px" }} >
                    <Box>
                        <Typography gutterBottom variant="h1" fontWeight="600" component="h2" fontSize="16px">{selectedValue.assignment_name}</Typography>
                        <Box display="flex" alignItems="center" flexWrap="wrap" gap="5px">
                            <CustomChip tooltipTitle={isAssignmentActive ? "Students can still submit their submissions" : "Cannot receive submissions anymore"} variant='filled' color={isAssignmentActive ? "primary" : "error"} value={isAssignmentActive ? "Active" : "Ended"} />
                            <CustomChip tooltipTitle="Assignment submission mode" value={selectedValue.assignment_submission_mode} />
                            <CustomChip tooltipTitle={`Created at: ${formatDate( selectedValue.assignment_created_at, { format: "LLL" }, dayjs( selectedValue.assignment_created_at ) < dayjs( ASSIGNMENT_CONVERT_DUE_TIME_BEFORE ) ? { hours: 5, minutes: 30 } : 0 )}`} value={`Created ${getTimePassedFrom( selectedValue.assignment_created_at )}`} />
                        </Box>
                    </Box>
                    <IconButton onClick={handleClose}>{Icons.default.CloseIcon}</IconButton>
                </Box>
                <Divider />
                <DialogContent sx={{ marginBottom: "20px", display: "flex", flexDirection: "column", gap: "20px" }}>

                    {['admin', 'subadmin'].includes( UserStore.getUser?.user_role?.toLowerCase() ) && <Box border={border[1]} borderRadius={layout.borderRadius} padding="10px" display="flex" flexDirection="column" >
                        <Typography color='error' fontWeight="600">Danger Zone</Typography>
                        <Typography variant='subtitle2' fontSize="12px" gutterBottom display="block" color='textSecondary'>Delete this assignment and all of its student submissions.</Typography>
                        <Button onClick={() => setConfirmDialogStatus( true )} sx={{ textTransform: "capitalize", width: "fit-content", marginTop: "10px" }} startIcon={Icons.default.DeleteIcon} color="errorMessage" variant='contained' disableElevation>Delete</Button>
                    </Box>}

                    <CountCard icon={Icons.default.Calendar} color="error" label="Due On" value={
                        dayjs( selectedValue.assignment_created_at ) < dayjs( ASSIGNMENT_CONVERT_DUE_TIME_BEFORE ) ?
                            `${dayjs( selectedValue.assignment_due_date ).format( "DD MMM YYYY" )}, 11:59 PM` :
                            dayjs( selectedValue.assignment_due_date ).format( "DD MMM YYYY, hh:mm A" )
                    } />

                    <Typography fontSize="14px">{selectedValue.assignment_details}</Typography>

                    {selectedValue.assignment_file_paths && selectedValue.assignment_file_paths.length > 0 && <Box display="flex" gap="10px" flexDirection="column">
                        {selectedValue.assignment_file_paths.map( ( item, index ) => {
                            const fileDetails = item.match( SPACES_LOCATION_REGEXP ).groups
                            return (
                                <Box gap="20px" flexWrap="wrap" color="secondary.main" key={`item-${index}`} width="fit-content" display="flex" alignItems="center" justifyContent="space-between" borderRadius="5px" bgcolor={palette.contentBg} border={border[1]} padding={{ sm: "5px 10px", xs: "5px" }} sx={{ cursor: "pointer" }}>
                                    <Box maxWidth={{ md: "inital", xs: "90%" }} display="flex" overflow="hidden" gap="5px" alignItems="center">
                                        <Box marginTop="5px">
                                            <img style={{ objectFit: "fit" }} width="35px" src={attachmentThumbnails[fileDetails?.fileextention?.toLowerCase()] || attachmentThumbnails["default"]} alt={item.split( "/" ).pop().replace( /%\d+/g, " " )} />
                                        </Box>
                                        <Box overflow="hidden">
                                            <Typography title={decodeURIComponent( fileDetails?.filefullname )} noWrap variant="subtitle2">{decodeURIComponent( fileDetails?.filefullname )}</Typography>
                                            <Typography variant="body2" fontSize="10px">{extentionsToNameMap[fileDetails?.fileextention?.toLowerCase()] || ""}</Typography>
                                        </Box>
                                    </Box>
                                    <Box>
                                        <IconButton disabled={item === downloadingFile} onClick={() => handleListItemClick( item )} size="small">{item === downloadingFile ? <CircularProgress size={20} /> : Icons.default.DownloadIcon}</IconButton>
                                        {!isIOS() && <IconButton onClick={() => viewAttachment( item )} size="small">{Icons.default.OpenInNewIcon}</IconButton>}
                                    </Box>
                                </Box>
                            )
                        } )}
                    </Box>}

                </DialogContent>
            </Box>}
            {
                loaded && !selectedValue && <Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" padding={{ sm: "20px", xs: "10px" }} borderBottom="1px solid #d3d3d3">
                        <Box display="flex" gap="20px" alignItems="center">
                            <WarningAmberRounded />
                            <Typography variant="h6">Access denied</Typography>
                        </Box>
                        <IconButton sx={{ color: "white" }} onClick={handleClose}>{Icons.default.CloseIcon}</IconButton>
                    </Box>
                    <Box padding={{ sm: "20px", xs: "10px" }} display="flex" flexDirection="column" gap="20px" alignItems="center">
                        <img width="200px" src={noPermissionImg} alt="No permissions" />
                        <Typography variant='subtitle2' fontWeight="normal" textAlign="center">You doesn't have the permission to view the assignment with ID <em><strong>{selectedAssignmentId}</strong></em> or this assignment has already been deleted. </Typography>
                    </Box>
                </Box>
            }
        </Dialog >
    )
}

AssignmentViewer.propTypes = {
    onClose: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
}

export default AssignmentViewer